import {
  computed,
  onMounted,
  provide,
  ref,
} from 'vue'
import { useContextStore } from 'src/store'
import mapAnalysesCardItem, {
  mapAnalysesPartnerCardItem,
} from 'src/components/pages/[town-translit]/diagnostika/analiz/_functions/mapAnalysesCardItem'
import buildPageHref from 'src/components/pages/[town-translit]/diagnostika/analiz/_functions/buildPageHref'
import {
  getAnalysesCardsPage,
  getAnalysesInitialExtras,
  isAnalysesFiltersEnabled,
} from 'src/components/pages/[town-translit]/diagnostika/analiz/_functions/getAnalysesCardsContext'
import { getAnalysesLpuCardsFiltersFromContext } from 'src/components/pages/[town-translit]/diagnostika/analiz/_functions/getAnalysesLpuCardsFiltersFromContext'
import useAnalysesLpuCardLinks from 'src/components/pages/[town-translit]/diagnostika/analiz/components/LpuCard/_composables/useAnalysesLpuCardLinks'
import apiAnalysesLpuCardsFilter
  from 'src/components/pages/[town-translit]/diagnostika/analiz/api/apiAnalysesLpuCardsFilter'
import {
  ANALYSES_LPU_DEFAULT_SORT,
  type AnalysesLpuSortValue,
} from 'src/components/pages/[town-translit]/diagnostika/analiz/_constants/analyses-lpu-sort'
import type { AnalysesLpuCardsFilters } from 'src/components/pages/[town-translit]/diagnostika/analiz/api/apiAnalysesLpuCardsFilter.types'
import {
  buildAnalysesLpuCardsBaseUrl,
  buildAnalysesPlaceFilterUrl,
} from 'src/components/pages/[town-translit]/diagnostika/analiz/_functions/buildAnalysesPlaceFilterUrl'
import {
  type AnalysesPageContext,
  type LpuCardsPage,
} from 'src/components/pages/[town-translit]/diagnostika/analiz/_types'
import type { CommonContextStore } from 'src/types/context'

const LPU_CARDS_SECTION_ANCHOR = 'analyses-lpu-cards'

function buildAppliedFiltersQuery(filters: AnalysesLpuCardsFilters) {
  const query: Record<string, string> = {}

  if (filters.sort && filters.sort !== ANALYSES_LPU_DEFAULT_SORT) {
    query.sort = filters.sort
  }

  if (filters.onlineOrder) {
    query.online_order = 'true'
  }

  if (filters.withDiscount) {
    query.order_discount = 'true'
  }

  return query
}

function hasActiveAnalysesLpuFilters(filters: AnalysesLpuCardsFilters | null): boolean {
  if (!filters) {
    return false
  }

  return Boolean(
    filters.place
    || filters.onlineOrder
    || filters.withDiscount
    || (filters.sort && filters.sort !== ANALYSES_LPU_DEFAULT_SORT),
  )
}

function scrollToLpuCardsSection() {
  if (!process.env.IS_CLIENT) {
    return
  }

  document.getElementById(LPU_CARDS_SECTION_ANCHOR)?.scrollIntoView()
}

function syncAnalysesLpuCardsUrl(href: string) {
  if (!process.env.IS_CLIENT) {
    return
  }

  const url = href.replace(/#.*$/, '')

  window.history.replaceState(window.history.state, '', url)
}

export default function useAnalysesLpuCardsSection() {
  const { context } = useContextStore() as CommonContextStore & {
    context: AnalysesPageContext
  }
  const {
    townNamePred,
    lpuSetCardOptions,
    lpuCardLinkOptions,
  } = useAnalysesLpuCardLinks()

  const isMobile = computed(() => context.flavour === 'mobile')
  const isFiltersEnabled = isAnalysesFiltersEnabled(context)
  const initialFilters = isFiltersEnabled
    ? getAnalysesLpuCardsFiltersFromContext(context)
    : null

  const lpuCardsPage = ref<LpuCardsPage>(getAnalysesCardsPage(context, isFiltersEnabled))
  const appliedFilters = ref<AnalysesLpuCardsFilters | null>(initialFilters)
  const appliedSort = ref<AnalysesLpuSortValue>(
    initialFilters?.sort || ANALYSES_LPU_DEFAULT_SORT,
  )
  const appliedOnlineOrder = ref(Boolean(initialFilters?.onlineOrder))
  const appliedWithDiscount = ref(Boolean(initialFilters?.withDiscount))
  const isFiltersApplying = ref(false)
  const isRequestErrorDialogOpen = ref(false)
  const requestError = ref<unknown>()

  const appliedPlace = computed(() => appliedFilters.value?.place)

  provide('isMobile', isMobile.value)

  onMounted(() => {
    if (!process.env.IS_CLIENT || window.location.hash !== `#${LPU_CARDS_SECTION_ANCHOR}`) {
      return
    }

    scrollToLpuCardsSection()
  })

  const currentPage = computed(() => lpuCardsPage.value?.number || 1)

  const showInitialExtras = computed(() => (
    currentPage.value === 1 && !hasActiveAnalysesLpuFilters(appliedFilters.value)
  ))

  const pageRange = computed(() => {
    const range = lpuCardsPage.value?.page_range
      || lpuCardsPage.value?.paginator?.page_range

    if (range?.length) {
      return range
    }

    const numPages = lpuCardsPage.value?.paginator?.num_pages || 1

    return Array.from({ length: numPages }, (_, index) => index + 1)
  })

  const lastPageNumber = computed(() => {
    const pages = pageRange.value.filter((page): page is number => Boolean(page))

    return pages[pages.length - 1] || currentPage.value
  })

  const showPagination = computed(() => (
    Boolean(lpuCardsPage.value?.has_other_pages)
    || pageRange.value.filter(Boolean).length > 1
  ))

  const hasPrevious = computed(() => (
    lpuCardsPage.value?.has_previous
    ?? currentPage.value > 1
  ))

  const hasNext = computed(() => (
    lpuCardsPage.value?.has_next
    ?? currentPage.value < lastPageNumber.value
  ))

  const previousPageNumber = computed(() => (
    lpuCardsPage.value?.previous_page_number
    ?? (currentPage.value > 1 ? currentPage.value - 1 : null)
  ))

  const nextPageNumber = computed(() => (
    lpuCardsPage.value?.next_page_number
    ?? (currentPage.value < lastPageNumber.value ? currentPage.value + 1 : null)
  ))

  const getPageHref = (page: number) => {
    const filters = appliedFilters.value

    if (filters) {
      const townTranslit = context.town?.translit || ''
      const path = filters.place?.translit
        ? buildAnalysesPlaceFilterUrl(townTranslit, filters.place)
        : buildAnalysesLpuCardsBaseUrl(townTranslit)

      return buildPageHref({
        path,
        page,
        query: buildAppliedFiltersQuery(filters),
        hash: LPU_CARDS_SECTION_ANCHOR,
      })
    }

    return buildPageHref({
      path: context.request.path || '',
      page,
      query: context.request.GET,
      hash: LPU_CARDS_SECTION_ANCHOR,
    })
  }

  const mapCardOptions = computed(() => ({
    isFiltersEnabled,
    lpuCardOptions: {
      serviceClassTranslit: context.service_class.translit,
      country: context.request.country,
      currencySymbol: context.currency?.symbol,
      currentAwardYear: context.site_settings?.current_award_year,
      townTranslit: context.town?.translit,
      isSuperuser: context.request.user?.is_superuser,
      ...lpuCardLinkOptions,
    },
    lpuSetCardOptions: lpuSetCardOptions.value,
  }))

  const specialPlacementCards = computed(() => {
    const placementCards = (context.lpu_cards_placement || []).map(item => (
      mapAnalysesCardItem(item, {
        ...mapCardOptions.value,
        isSpecialPlacement: true,
      })
    ))

    const partnerCards = getAnalysesInitialExtras(context, isFiltersEnabled).map(item => (
      mapAnalysesPartnerCardItem(item, lpuSetCardOptions.value)
    ))

    return [...placementCards, ...partnerCards]
  })

  const pageCards = computed(() => {
    // eslint-disable-next-line no-underscore-dangle
    const items = lpuCardsPage.value?.__items__ || []

    return items.map(item => mapAnalysesCardItem(item, mapCardOptions.value))
  })

  const showNotFound = computed(() => (
    hasActiveAnalysesLpuFilters(appliedFilters.value)
    && !pageCards.value.length
  ))

  const showSection = computed(() => (
    Boolean(pageCards.value.length)
    || showInitialExtras.value
    || showNotFound.value
  ))

  function setAppliedFilters(filters: AnalysesLpuCardsFilters) {
    appliedFilters.value = {
      sort: filters.sort || ANALYSES_LPU_DEFAULT_SORT,
      onlineOrder: Boolean(filters.onlineOrder),
      withDiscount: Boolean(filters.withDiscount),
      place: filters.place,
    }
    appliedSort.value = appliedFilters.value.sort || ANALYSES_LPU_DEFAULT_SORT
    appliedOnlineOrder.value = Boolean(appliedFilters.value.onlineOrder)
    appliedWithDiscount.value = Boolean(appliedFilters.value.withDiscount)
  }

  async function applyFilters(filters: AnalysesLpuCardsFilters) {
    if (isFiltersApplying.value) {
      return
    }

    isFiltersApplying.value = true

    try {
      setAppliedFilters(filters)
      lpuCardsPage.value = await apiAnalysesLpuCardsFilter.apply({
        ...filters,
        townTranslit: context.town?.translit || '',
      })
      syncAnalysesLpuCardsUrl(getPageHref(lpuCardsPage.value.number || 1))
      scrollToLpuCardsSection()
    } catch (error) {
      openRequestErrorDialog(error)
    }

    // eslint-disable-next-line require-atomic-updates
    isFiltersApplying.value = false
  }

  async function goToPage(page: number) {
    if (isFiltersApplying.value || page === currentPage.value) {
      return
    }

    isFiltersApplying.value = true

    try {
      const href = getPageHref(page)
      const url = href.replace(/#.*$/, '')

      lpuCardsPage.value = await apiAnalysesLpuCardsFilter.applyByUrl(url)
      syncAnalysesLpuCardsUrl(getPageHref(lpuCardsPage.value.number || page))
      scrollToLpuCardsSection()
    } catch (error) {
      openRequestErrorDialog(error)
    }

    // eslint-disable-next-line require-atomic-updates
    isFiltersApplying.value = false
  }

  function openRequestErrorDialog(error: unknown) {
    requestError.value = error
    isRequestErrorDialogOpen.value = true
  }

  return {
    LPU_CARDS_SECTION_ANCHOR,
    context,
    isMobile,
    townNamePred,
    appliedSort,
    appliedOnlineOrder,
    appliedWithDiscount,
    appliedPlace,
    isFiltersApplying,
    showInitialExtras,
    showNotFound,
    showSection,
    specialPlacementCards,
    pageCards,
    showPagination,
    currentPage,
    pageRange,
    hasPrevious,
    hasNext,
    previousPageNumber,
    nextPageNumber,
    getPageHref,
    isRequestErrorDialogOpen,
    requestError,
    applyFilters,
    goToPage,
    openRequestErrorDialog,
  }
}
